import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PropertyForm from '../components/PropertyForm';
import Map from '../components/Map';
import EstimatePanel from '../components/EstimatePanel';
import { calculateEstimate } from '../lib/estimator';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiMapPin, FiHome, FiCalculator } = FiIcons;

const Simulator = () => {
  const [formData, setFormData] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [address, setAddress] = useState('');
  const [progress, setProgress] = useState(10);
  const { t } = useTranslation();

  useEffect(() => {
    const requiredFields = [
      'propertyType', 'address', 'sqMeters', 'rooms', 'bathrooms', 'guests', 
      'periodType', 'firstName', 'lastName', 'phone', 'email', 'gdprConsent'
    ];
    const filledFields = requiredFields.filter(field => {
      if (field === 'address') return address;
      if (field === 'gdprConsent') return formData[field] === true;
      return formData[field];
    });
    const newProgress = Math.max(10, Math.round((filledFields.length / requiredFields.length) * 100));
    setProgress(newProgress);
  }, [formData, address]);

  const handleCalculateEstimate = () => {
    if (!formData.propertyType || !address || !formData.sqMeters || !formData.rooms) return;
    const estimateData = { ...formData, address, coordinates };
    const result = calculateEstimate(estimateData);
    setEstimate(result);
    setTimeout(() => {
      document.getElementById('estimate-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="pt-16 min-h-screen bg-brand-silver/10">
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-8 shadow-2xl bg-brand-blue border-4 border-brand-gold"
            >
              <SafeIcon icon={FiCalculator} className="h-12 w-12 text-brand-gold" />
            </motion.div>
            <h1 className="text-4xl md:text-6xl font-black text-brand-blue mb-6 uppercase tracking-tight">
              Simulator ROI <span className="text-brand-gold">Castello</span>
            </h1>
            <p className="text-xl md:text-2xl text-brand-subtle mb-10 max-w-3xl mx-auto font-light leading-relaxed">
              Analisi data-driven del potenziale economico del tuo immobile. <br/> Ottieni una stima professionale in tempo reale.
            </p>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto mb-12">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm font-bold text-brand-blue uppercase tracking-widest">Analisi Immobile</span>
                <span className="text-sm font-bold text-brand-gold">{progress}%</span>
              </div>
              <div className="w-full bg-white rounded-full h-4 shadow-inner p-1 border border-brand-silver">
                <div className="h-full rounded-full transition-all duration-700 bg-brand-gold shadow-lg shadow-brand-gold/30" style={{ width: `${progress}%` }} />
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="bg-white rounded-3xl p-8 shadow-2xl border border-brand-silver/30">
                <PropertyForm 
                  onFormChange={setFormData} 
                  onAddressChange={setAddress} 
                  onCoordinatesChange={setCoordinates} 
                  onCalculate={handleCalculateEstimate} 
                  formData={formData} 
                  address={address} 
                  canCalculate={progress >= 60} 
                />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-brand-silver/30">
                <div className="p-6 bg-brand-blue text-white">
                  <h3 className="text-xl font-bold flex items-center">
                    <SafeIcon icon={FiMapPin} className="mr-3 text-brand-gold" /> Geo-Localizzazione
                  </h3>
                </div>
                <div className="h-[350px]">
                  <Map coordinates={coordinates} onCoordinatesChange={setCoordinates} address={address} onAddressChange={setAddress} />
                </div>
              </div>

              {estimate && (
                <div id="estimate-results" className="animate-fade-in">
                  <EstimatePanel estimate={estimate} formData={{ ...formData, address, coordinates }} />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Simulator;